class ProductQuiz {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.config = window.quizConfig[this.sectionId];
    
    if (!this.config) {
      console.error('Quiz config not found for section:', this.sectionId);
      return;
    }

    this.state = {
      currentQuestionId: null,
      currentStep: 1,
      answers: {},
      questionHistory: [],
      totalQuestions: Object.keys(this.config.questions).length
    };

    this.elements = {
      questions: container.querySelectorAll('.question-slide'),
      images: container.querySelectorAll('.quiz-image'),
      progressBars: container.querySelectorAll('.progress-bar'),
      nextBtn: container.querySelector('.btn-next'),
      backBtn: container.querySelector('.btn-back'),
      retakeBtn: container.querySelector('.btn-retake'),
      answerButtons: container.querySelectorAll('.answer-btn'),
      resultsSection: container.querySelector('.quiz-results'),
      resultsGrid: container.querySelector('#quiz-results-grid'),
      emailSection: container.querySelector('.quiz-email-collection'),
      emailForm: container.querySelector('#quiz-email-form'),
      emailInput: container.querySelector('#quiz-email-input'),
      emailSuccessMsg: container.querySelector('.email-success-message')
    };

    this.init();
  }

  init() {
    const firstQuestion = this.getFirstQuestion();
    
    if (!firstQuestion) {
      console.error('No valid questions found. Please check your quiz configuration.');
      return;
    }
    
    const questionIds = Object.keys(this.config.questions);
    const duplicateCheck = new Set(questionIds);
    if (duplicateCheck.size !== questionIds.length) {
      console.error('Duplicate Question IDs detected! Each question must have a unique ID.');
    }
    
    // 如果未设置，默认启用邮箱收集
    if (this.config.settings && this.config.settings.enableEmailCollection === undefined) {
      this.config.settings.enableEmailCollection = true;
    }
    
    this.state.currentQuestionId = firstQuestion.id;
    this.state.currentQuestionIndex = 1;
    this.state.questionHistory.push(firstQuestion.id);

    this.restoreState();
    this.showQuestion(firstQuestion.id);
    this.bindEvents();
  }

  bindEvents() {
    this.elements.answerButtons = this.container.querySelectorAll('.answer-btn');
    
    this.elements.answerButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const button = e.currentTarget;
        const questionId = button.closest('.answer-buttons').dataset.questionId;
        const value = button.dataset.value;
        
        const allButtons = button.closest('.answer-buttons').querySelectorAll('.answer-btn');

        if (button.classList.contains('selected')) {
          button.classList.remove('selected');
          delete this.state.answers[questionId];
        } else {
          allButtons.forEach(b => b.classList.remove('selected'));
          button.classList.add('selected');
          this.saveAnswer(questionId, value);
        }
        
        this.updateButtons();
      });
    });

    this.elements.nextBtn.addEventListener('click', () => this.handleNext());
    this.elements.backBtn.addEventListener('click', () => this.handleBack());
    
    if (this.elements.retakeBtn) {
      this.elements.retakeBtn.addEventListener('click', () => this.reset());
    }
    
    if (this.elements.emailForm) {
      this.elements.emailForm.addEventListener('submit', (e) => this.handleEmailSubmit(e));
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this.elements.nextBtn.disabled) {
        this.handleNext();
      }
    });
  }

  getFirstQuestion() {
    const questions = Object.values(this.config.questions);
    return questions.sort((a, b) => a.order - b.order)[0];
  }

  showQuestion(questionId) {
    this.elements.questions.forEach(q => {
      q.dataset.active = 'false';
      q.style.display = 'none';
    });
    this.elements.images.forEach(img => {
      img.dataset.active = 'false';
    });

    if (this.elements.resultsSection) {
      this.elements.resultsSection.dataset.active = 'false';
      this.elements.resultsSection.style.display = 'none';
    }

    const currentQuestion = this.container.querySelector(
      `.question-slide[data-question="${questionId}"]`
    );
    const currentImage = this.container.querySelector(
      `.quiz-image[data-question="${questionId}"]`
    );

    if (currentQuestion) {
      currentQuestion.dataset.active = 'true';
      currentQuestion.style.display = 'block';
      currentQuestion.style.animation = 'fadeIn 0.4s ease-in-out';
    }

    if (currentImage) {
      currentImage.dataset.active = 'true';
      currentImage.style.animation = 'fadeIn 0.5s ease-in-out';
    }

    this.updateProgress();
    this.updateButtons();
    this.restoreAnswer(questionId);
  }

  saveAnswer(questionId, value) {
    this.state.answers[questionId] = value;
    this.saveState();
    this.updateProgress();
    this.updateButtons();

    if (this.config.settings.enableAnalytics) {
      this.trackAnswer(questionId, value);
    }
  }

  restoreAnswer(questionId) {
    if (this.state.answers[questionId]) {
      const answerButtons = this.container.querySelector(
        `.answer-buttons[data-question-id="${questionId}"]`
      );
      if (answerButtons) {
        const selectedBtn = answerButtons.querySelector(
          `.answer-btn[data-value="${this.state.answers[questionId]}"]`
        );
        if (selectedBtn) {
          selectedBtn.classList.add('selected');
        }
      }
    }
  }

  handleNext() {
    const nextQuestionId = this.getNextQuestion();

    if (nextQuestionId === 'results') {
      // 默认启用邮箱收集，除非明确设置为 false
      const emailEnabled = this.config.settings?.enableEmailCollection !== false;

      
      if (emailEnabled) {
        this.showEmailCollection();
      } else {
        this.showResults();
      }
    } else {
      this.state.currentQuestionId = nextQuestionId;
      this.state.currentStep++;
      this.state.questionHistory.push(nextQuestionId);
      this.showQuestion(nextQuestionId);
    }
  }

  handleBack() {
    if (this.state.questionHistory.length <= 1) {
      return;
    }

    this.state.questionHistory.pop();
    const previousQuestionId = this.state.questionHistory[this.state.questionHistory.length - 1];
    
    this.state.currentQuestionId = previousQuestionId;
    this.state.currentStep--;
    this.showQuestion(previousQuestionId);
  }

  getNextQuestion() {
    const questions = Object.values(this.config.questions);
    const currentQuestion = this.config.questions[this.state.currentQuestionId];
    const nextQuestions = questions.filter(q => q.order > currentQuestion.order);
    
    if (nextQuestions.length === 0) {
      return 'results';
    }

    const nextQuestion = nextQuestions.sort((a, b) => a.order - b.order)[0];
    return nextQuestion.id;
  }

  showResults() {
    const recommendedProducts = this.calculateRecommendations();

    this.elements.questions.forEach(q => {
      q.dataset.active = 'false';
      q.style.display = 'none';
    });
    this.elements.images.forEach(img => {
      img.dataset.active = 'false';
    });
    
    if (this.elements.emailSection) {
      this.elements.emailSection.dataset.active = 'false';
      this.elements.emailSection.style.display = 'none';
    }

    const imageContainer = this.container.querySelector('.quiz-image-container');
    if (imageContainer) {
      imageContainer.style.display = 'none';
    }
    
    const progressContainer = this.container.querySelector('.quiz-progress');
    if (progressContainer) {
      progressContainer.style.display = 'none';
    }

    const quizContainer = this.container.querySelector('.product-quiz');
    if (quizContainer) {
      quizContainer.style.gridTemplateColumns = '1fr';
    }

    this.elements.nextBtn.style.display = 'none';
    this.elements.backBtn.style.display = 'none';

    if (this.elements.resultsSection) {
      this.elements.resultsSection.dataset.active = 'true';
      this.elements.resultsSection.style.display = 'block';
      this.elements.resultsSection.style.animation = 'fadeIn 0.5s ease-in-out';
    }

    this.renderProducts(recommendedProducts);

    this.elements.progressBars.forEach(bar => {
      bar.style.backgroundColor = '#000000';
    });

    if (this.config.settings.enableAnalytics) {
      this.trackCompletion(recommendedProducts);
    }
  }
  
  showEmailCollection() {
    this.elements.questions.forEach(q => {
      q.dataset.active = 'false';
      q.style.display = 'none';
    });
    this.elements.images.forEach(img => {
      img.dataset.active = 'false';
    });

    const imageContainer = this.container.querySelector('.quiz-image-container');
    if (imageContainer) {
      imageContainer.style.display = 'none';
    }
    
    const progressContainer = this.container.querySelector('.quiz-progress');
    if (progressContainer) {
      progressContainer.style.display = 'none';
    }

    const quizContainer = this.container.querySelector('.product-quiz');
    if (quizContainer) {
      quizContainer.style.gridTemplateColumns = '1fr';
    }

    this.elements.nextBtn.style.display = 'none';
    this.elements.backBtn.style.display = 'none';

    if (this.elements.emailSection) {
      this.elements.emailSection.dataset.active = 'true';
      this.elements.emailSection.style.display = 'block';
      this.elements.emailSection.style.animation = 'fadeIn 0.5s ease-in-out';
      
      // 滚动到邮箱输入区域
      setTimeout(() => {
        this.elements.emailSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
        
        // 聚焦到邮箱输入框
        if (this.elements.emailInput) {
          this.elements.emailInput.focus();
        }
      }, 100);
    } else {
      console.error('❌ Email section element not found!');
    }

    this.elements.progressBars.forEach(bar => {
      bar.style.backgroundColor = '#000000';
    });
  }
  
  async handleEmailSubmit(e) {
    e.preventDefault();
    
    const submitBtn = e.target.querySelector('.btn');
    const originalText = submitBtn.textContent;
    const email = document.getElementById('quiz-email-input').value;
    
    // 禁用按钮
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
    
    try {
      // 格式化quiz答案
      const quizAnswersText = this.formatAnswersForShopify();
      const recommendedProducts = this.calculateRecommendations();
      
      // 保存到Shopify（通过Storefront API）
      await this.saveToShopify(email, quizAnswersText, recommendedProducts);
      
      // 显示结果
      this.showResults();
      
    } catch (error) {
      console.error('❌ Error:', error);
      alert('保存数据时出错，但您仍可以查看结果。');
      this.showResults();
    }
  }
  
  async saveToShopify(email, quizAnswers, products) {
    try {
      // 格式化问答对 - 每个问题单独一个属性
      const formattedAnswers = [];
      for (const [questionId, answer] of Object.entries(this.state.answers)) {
        const question = this.config.questions[questionId];
        if (question) {
          formattedAnswers.push({
            question: question.text,
            answer: answer
          });
        }
      }
      
      // 获取产品详细信息
      const productDetails = products.map(p => {
        // 尝试从DOM获取产品信息
        const productCard = document.querySelector(`[data-product-handle="${p.handle}"]`);
        let imageUrl = '';
        let productUrl = `https://${this.config.settings.shopDomain}/products/${p.handle}`;
        let title = p.handle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        let price = '';
        
        if (productCard) {
          const imgEl = productCard.querySelector('img');
          const linkEl = productCard.querySelector('a[href*="/products/"]');
          const titleEl = productCard.querySelector('.product-title, .card-title, h3, h2');
          const priceEl = productCard.querySelector('.price-item--sale, .price-item--regular, .price, [class*="price"]');
          
          if (imgEl) {
            imageUrl = imgEl.src || imgEl.dataset.src || imgEl.getAttribute('srcset')?.split(' ')[0] || '';
          }
          if (linkEl) {
            productUrl = linkEl.href;
          }
          if (titleEl) {
            title = titleEl.textContent.trim();
          }
          if (priceEl) {
            // 提取价格文本，移除多余空格和换行符
            price = priceEl.textContent.trim().replace(/\s+/g, ' ');
          }
        }
        
        return {
          ProductHandle: p.handle,
          ProductName: title,
          ProductURL: productUrl,
          ProductImage: imageUrl || `https://${this.config.settings.shopDomain}/cdn/shop/products/placeholder.png`,
          ProductPrice: price
        };
      });
      
      const productHandles = products.map(p => p.handle).join(', ');
      
      // 发送到 Klaviyo 使用客户端方法
      if (this.config.settings.klaviyoApiKey && this.config.settings.klaviyoApiKey.trim() !== '') {
        try {
          // 使用 Klaviyo 的 _learnq 全局对象 (如果已加载)
          if (typeof window._learnq !== 'undefined') {
            window._learnq.push(['identify', {
              '$email': email,
              'quiz_completed': true
            }]);
            
            const eventData = {
              'QuizAnswers': formattedAnswers,
              'RecommendedProducts': productDetails,
              'ProductHandles': productHandles,
              'ProductCount': products.length,
              'ShopDomain': this.config.settings.shopDomain
            };
            
            window._learnq.push(['track', 'Quiz Completed', eventData]);
          } else {
            // 备用方法: 使用 Klaviyo 的 identify.js 端点
            
            const identifyData = {
              token: this.config.settings.klaviyoApiKey,
              properties: {
                '$email': email,
                'quiz_completed': true
              }
            };
            
            // 使用 GET 请求通过 JSONP 方式发送
            const identifyUrl = 'https://a.klaviyo.com/api/identify?' + 
              'data=' + encodeURIComponent(btoa(JSON.stringify(identifyData)));
            
            const img = new Image();
            img.src = identifyUrl;
            
            // 发送 track 事件
            const trackData = {
              token: this.config.settings.klaviyoApiKey,
              event: 'Quiz Completed',
              customer_properties: {
                '$email': email
              },
              properties: {
                'QuizAnswers': formattedAnswers,
                'RecommendedProducts': productDetails,
                'ProductHandles': productHandles,
                'ProductCount': products.length,
                'ShopDomain': this.config.settings.shopDomain
              }
            };
            
            const trackUrl = 'https://a.klaviyo.com/api/track?' + 
              'data=' + encodeURIComponent(btoa(JSON.stringify(trackData)));
            
            const trackImg = new Image();
            trackImg.src = trackUrl;
          }
        } catch (klaviyoError) {
          console.error('❌ Klaviyo error:', klaviyoError);
        }
      } else {
        console.error('❌ Klaviyo API Key not configured or empty!');
      }
      
      // 保存到localStorage作为备份
      this.saveQuizLocally(email, quizAnswers, products);
      
    } catch (error) {
      console.error('❌ Klaviyo error:', error);
      // 即使Klaviyo失败也继续显示结果
    }
  }
  
  saveQuizLocally(email, quizAnswers, products) {
    try {
      const submissions = JSON.parse(localStorage.getItem('quiz_submissions') || '[]');
      submissions.push({
        email,
        answers: this.state.answers,
        answersText: quizAnswers,
        products: products.map(p => ({ id: p.id, handle: p.handle })),
        timestamp: new Date().toISOString()
      });
      if (submissions.length > 50) submissions.shift();
      localStorage.setItem('quiz_submissions', JSON.stringify(submissions));
    } catch (e) {
      console.warn('LocalStorage error:', e);
    }
  }
  
  formatAnswersForShopify() {
    let text = '=== Quiz Results ===\n\n';
    
    for (const [questionId, answer] of Object.entries(this.state.answers)) {
      const question = this.config.questions[questionId];
      if (question) {
        text += `Question: ${question.text}\n`;
        text += `Answer: ${answer}\n\n`;
      }
    }
    
    text += `Submitted: ${new Date().toLocaleString()}\n`;
    
    return text;
  }

  calculateRecommendations() {
    const rules = this.config.productRules;
    const matchedRules = [];

    rules.forEach(rule => {
      let matches = true;
      let matchCount = 0;

      for (const [questionId, expectedValue] of Object.entries(rule.conditions)) {
        // 支持匹配"未回答"状态
        if (expectedValue === 'unanswered') {
          if (this.state.answers[questionId]) {
            matches = false;
            break;
          }
          matchCount++;
        } else if (expectedValue) {
          // 支持单个值或多个值（OR关系）
          const expectedValues = Array.isArray(expectedValue) ? expectedValue : [expectedValue];
          const userAnswer = this.state.answers[questionId];
          
          // 检查用户答案是否匹配任一期望值（OR关系）
          if (!expectedValues.includes(userAnswer)) {
            matches = false;
            break;
          }
          matchCount++;
        }
      }

      if (matches && matchCount > 0) {
        matchedRules.push({
          ...rule,
          matchCount: matchCount
        });
      }
    });

    matchedRules.sort((a, b) => {
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      return b.matchCount - a.matchCount;
    });

    if (matchedRules.length > 0) {
      const bestMatch = matchedRules[0];
      return bestMatch.products;
    }

    return [];
  }

  renderProducts(products) {
    if (!this.elements.resultsGrid) return;

    // 隐藏所有产品卡片
    const allCards = this.elements.resultsGrid.querySelectorAll('.quiz-product-card');
    allCards.forEach(card => card.style.display = 'none');

    // 显示或隐藏"无结果"消息
    const noResultsMsg = this.container.querySelector('.no-results-message');
    
    if (products.length === 0) {
      if (this.config.settings.showNoResults && noResultsMsg) {
        noResultsMsg.style.display = 'block';
      }
      return;
    }

    if (noResultsMsg) {
      noResultsMsg.style.display = 'none';
    }

    // 显示匹配的产品卡片（去重）
    const productIds = products.map(p => p.id);
    let displayedCount = 0;
    const displayedProductIds = new Set();
    
    allCards.forEach(card => {
      const productId = parseInt(card.dataset.productId);
      
      if (productIds.includes(productId) && !displayedProductIds.has(productId)) {
        card.style.display = 'block';
        card.style.animation = `fadeInUp 0.5s ease-out ${displayedCount * 0.1}s both`;
        displayedProductIds.add(productId);
        displayedCount++;
      }
    });
  }

  updateProgress() {
    const questionIds = Object.keys(this.config.questions).sort((a, b) => {
      return this.config.questions[a].order - this.config.questions[b].order;
    });
    
    const currentIndex = questionIds.indexOf(this.state.currentQuestionId);
    
    this.elements.progressBars.forEach((bar, index) => {
      if (index <= currentIndex) {
        bar.style.backgroundColor = '#000000';
      } else {
        bar.style.backgroundColor = '#e2e2e2';
      }
    });
  }

  updateButtons() {
    this.elements.nextBtn.disabled = false;
    if (this.state.questionHistory.length <= 1) {
      this.elements.backBtn.style.display = 'none';
      this.elements.backBtn.disabled = true;
      this.elements.nextBtn.style.flex = '0 0 calc(50% - 8px)';
      this.elements.nextBtn.style.marginLeft = '0';
    } else {
      this.elements.backBtn.style.display = '';
      this.elements.backBtn.disabled = false;
      this.elements.nextBtn.style.flex = '';
      this.elements.nextBtn.style.marginLeft = '';
    }
    
    const nextQuestionId = this.getNextQuestion();
    if (nextQuestionId === 'results') {
      this.elements.nextBtn.textContent = 'See Results';
    } else {
      this.elements.nextBtn.textContent = 'Next';
    }
  }

  reset() {
    this.state = {
      currentQuestionId: null,
      currentStep: 1,
      answers: {},
      questionHistory: [],
      totalQuestions: this.state.totalQuestions
    };

    sessionStorage.removeItem(`quiz_state_${this.sectionId}`);

    this.elements.answerButtons.forEach(btn => {
      btn.classList.remove('selected');
    });
    
    // 隐藏结果页面
    if (this.elements.resultsSection) {
      this.elements.resultsSection.dataset.active = 'false';
      this.elements.resultsSection.style.display = 'none';
    }
    
    // 隐藏邮箱收集页面
    if (this.elements.emailSection) {
      this.elements.emailSection.dataset.active = 'false';
      this.elements.emailSection.style.display = 'none';
      
      // 重置邮箱表单
      if (this.elements.emailForm) {
        this.elements.emailForm.reset();
        this.elements.emailForm.style.display = '';
      }
      if (this.elements.emailSuccessMsg) {
        this.elements.emailSuccessMsg.style.display = 'none';
      }
    }
    
    // 隐藏所有产品卡片
    const allCards = this.elements.resultsGrid?.querySelectorAll('.quiz-product-card');
    if (allCards) {
      allCards.forEach(card => {
        card.style.display = 'none';
      });
    }
    
    // 隐藏无结果消息
    const noResultsMsg = this.container.querySelector('.no-results-message');
    if (noResultsMsg) {
      noResultsMsg.style.display = 'none';
    }
    
    const imageContainer = this.container.querySelector('.quiz-image-container');
    if (imageContainer) {
      imageContainer.style.display = '';
    }
    
    const progressContainer = this.container.querySelector('.quiz-progress');
    if (progressContainer) {
      progressContainer.style.display = '';
    }

    const quizContainer = this.container.querySelector('.product-quiz');
    if (quizContainer) {
      quizContainer.style.gridTemplateColumns = '';
    }

    this.elements.nextBtn.style.display = '';
    this.elements.backBtn.style.display = '';
    this.elements.nextBtn.textContent = 'Next';

    const firstQuestion = this.getFirstQuestion();
    this.state.currentQuestionId = firstQuestion.id;
    this.state.questionHistory.push(firstQuestion.id);
    this.showQuestion(this.state.currentQuestionId);
  }

  saveState() {
    sessionStorage.setItem(
      `quiz_state_${this.sectionId}`,
      JSON.stringify(this.state)
    );
  }

  restoreState() {
    const savedState = sessionStorage.getItem(`quiz_state_${this.sectionId}`);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        this.state = { ...this.state, ...parsed };
      } catch (e) {
        console.error('Failed to restore quiz state:', e);
      }
    }
  }

  trackAnswer(questionId, value) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'quiz_answer',
        quiz_question: questionId,
        quiz_answer: value
      });
    }
  }

  trackCompletion(products) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'quiz_completed',
        quiz_answers: this.state.answers,
        quiz_products: products.map(p => p.handle)
      });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const quizContainers = document.querySelectorAll('.product-quiz-wrapper');
  
  quizContainers.forEach(container => {
    new ProductQuiz(container);
  });
});

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.product-quiz-wrapper');
    if (container) {
      new ProductQuiz(container);
    }
  });
}
